import { useCallback, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi, ApiError } from "../api/fastapi";
import {
  OptroTestCyclesResponseSchema,
  OptroGridResponseSchema,
  SampleUploadResultSchema,
  type OptroTestCycle,
  type OptroGridResponse,
  type ColumnSelection,
  type ColumnAttributeMapping,
  type SampleUploadResult,
} from "../models/audit/optroSampleImport";

interface UseOptroSampleImportOptions {
  sessionId: string;
}

/**
 * Wraps the three backend calls for the grid-based "Pull samples from Optro"
 * flow:
 *  1. list the linked control's test cycles (E1),
 *  2. fetch a chosen cycle's markup grid as detected columns + preview (E2),
 *  3. create the procedure's attributes + sample rows from the grid (E3).
 *
 * Mirrors `useSampleUpload` error/toast handling and per-call loading flags.
 * Create does NOT toast on success — the modal owns the Optro-specific success
 * wording so the CSV flow's toast stays untouched.
 */
export function useOptroSampleImport({
  sessionId,
}: UseOptroSampleImportOptions) {
  const [listLoading, setListLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // E1 — list the control's test cycles.
  const listTestCycles = useCallback(
    async (controlId: string): Promise<OptroTestCycle[] | null> => {
      setListLoading(true);
      try {
        const response = await fetchFastApi({
          method: "GET",
          path: `/controls/external/control/${controlId}/tests`,
        });
        const parsed = OptroTestCyclesResponseSchema.parse(response);
        return parsed.tests;
      } catch (error) {
        console.error("Failed to list Optro test cycles:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to load Optro test cycles");
        }
        return null;
      } finally {
        setListLoading(false);
      }
    },
    [],
  );

  // E2 — fetch a chosen cycle's grid.
  const getGrid = useCallback(
    async (
      controlId: string,
      testId: number,
    ): Promise<OptroGridResponse | null> => {
      setGridLoading(true);
      try {
        const response = await fetchFastApi({
          method: "GET",
          path: `/controls/external/control/${controlId}/tests/${testId}/grid`,
        });
        const parsed = OptroGridResponseSchema.parse(response);
        return parsed;
      } catch (error) {
        console.error("Error fetching Optro grid:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to load grid");
        }
        return null;
      } finally {
        setGridLoading(false);
      }
    },
    [],
  );

  // E3 — create samples from the grid (first upload: create attributes).
  const confirmFirstUpload = useCallback(
    async (
      testId: number,
      columnSelections: ColumnSelection[],
    ): Promise<SampleUploadResult | null> => {
      setConfirmLoading(true);
      try {
        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/${sessionId}/sample-upload/create-from-optro-grid`,
          body: {
            test_id: testId,
            column_selections: columnSelections,
          },
        });
        return SampleUploadResultSchema.parse(response);
      } catch (error) {
        console.error("Error creating samples from Optro grid:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create samples");
        }
        return null;
      } finally {
        setConfirmLoading(false);
      }
    },
    [sessionId],
  );

  // E3 — create samples from the grid (subsequent upload: map to existing).
  const confirmSubsequentUpload = useCallback(
    async (
      testId: number,
      columnMappings: ColumnAttributeMapping[],
    ): Promise<SampleUploadResult | null> => {
      setConfirmLoading(true);
      try {
        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/${sessionId}/sample-upload/create-from-optro-grid`,
          body: {
            test_id: testId,
            column_mappings: columnMappings,
          },
        });
        return SampleUploadResultSchema.parse(response);
      } catch (error) {
        console.error("Error creating samples from Optro grid:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create samples");
        }
        return null;
      } finally {
        setConfirmLoading(false);
      }
    },
    [sessionId],
  );

  return {
    listLoading,
    gridLoading,
    confirmLoading,
    listTestCycles,
    getGrid,
    confirmFirstUpload,
    confirmSubsequentUpload,
  };
}
